import { useCallback, useContext, useRef } from 'react';
import { WebSocketContext } from '../context/WebSocketContext';
import {
	getNewNotePayload,
	getTypingPayload,
	WEB_SOCKET_MESSAGE_TYPES,
	WebSocketPayloadProps,
} from '../utils/WebSocketUtils';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';

const TYPING_EVENT_INTERVAL = 2000;
const TYPING_STOP_INACTIVITY_THRESHOLD = 2000;

interface UseNotesBroadcastProps {
	entityIdName: string;
	entityId: string | number;
	entityType: string;
	newNoteEntityType: string;
	currentUser: any;
}

/**
 * Lightweight hook for sending WebSocket broadcast messages (e.g. new note notifications).
 * Extracted from useNotesWebSocket so it can be reused in inline reply contexts
 * without the full JOIN/LEFT/typing lifecycle.
 */
export function useNotesBroadcast({
	entityIdName,
	entityId,
	entityType,
	newNoteEntityType,
	currentUser,
}: UseNotesBroadcastProps) {
	const socketContext = useContext(WebSocketContext) as any;

	const sendWsMessage = useCallback(
		(message: any): boolean => {
			const ctx = socketContext;
			if (!ctx) return false;
			const { sendMessage, socket } = ctx;

			if (!sendMessage) return false;
			if (socket && socket.readyState !== WebSocket.OPEN) return false;

			try {
				const res = sendMessage(message);
				return res || true;
			} catch (e) {
				return false;
			}
		},
		[socketContext]
	);

	const onSendNote = useCallback(
		(message?: string, newNoteType?: string) => {
			const payloadProps: WebSocketPayloadProps = {
				entityIdName,
				entityId,
				currentUser,
				entityType,
				newNoteEntityType,
			};
			const broadCastData = getNewNotePayload(payloadProps, message, newNoteType);
			sendWsMessage(broadCastData);
		},
		[entityIdName, entityId, currentUser, entityType, newNoteEntityType, sendWsMessage]
	);

	const typingRef = useRef(false);

	const sendTypingEvent = useCallback(
		(messageType: string) => {
			const payloadProps: WebSocketPayloadProps = {
				entityIdName,
				entityId,
				currentUser,
				entityType,
				newNoteEntityType,
				messageType,
			};
			const broadCastData = getTypingPayload(payloadProps);
			sendWsMessage(broadCastData);
		},
		[entityIdName, entityId, currentUser, entityType, newNoteEntityType, sendWsMessage]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const throttledSendTyping = useCallback(
		throttle(() => sendTypingEvent(WEB_SOCKET_MESSAGE_TYPES.NOTE_TYPING), TYPING_EVENT_INTERVAL, {
			leading: true,
			trailing: false,
		}),
		[sendTypingEvent]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedStopTyping = useCallback(
		debounce(() => {
			typingRef.current = false;
			sendTypingEvent(WEB_SOCKET_MESSAGE_TYPES.NOTE_TYPING_STOP);
		}, TYPING_STOP_INACTIVITY_THRESHOLD),
		[sendTypingEvent]
	);

	const onTyping = useCallback(() => {
		if (!typingRef.current) {
			typingRef.current = true;
			sendTypingEvent(WEB_SOCKET_MESSAGE_TYPES.NOTE_TYPING);
		}
		throttledSendTyping();
		debouncedStopTyping();
	}, [sendTypingEvent, throttledSendTyping, debouncedStopTyping]);

	return { onSendNote, onTyping, sendWsMessage };
}
